import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '@/components/Ui-components/display-image';
import parse from 'html-react-parser';
import {colors, fontSize, fontWeight, letterSpacing, lineHeight, borderRadius, borderWidth, shadows, spacing} from '@/styles/tokens';

interface ProblemCard {
    icon: string | StaticImageData;
    iconSize: number;
    title: string;
    /** Optional pill tags between the title and the body copy. */
    tags?: string[];
    challenge: string;
    fix: string;
}

interface Web3ProblemCardsProps {
    heading: string;
    subheading: string;
    ourFixIcon: string | StaticImageData;
    cards: ProblemCard[];
    buttonText: string;
    openForm: () => void;
    /** Label above the card body. Pass an empty string to render the body with no label. */
    challengeLabel?: string;
    /** Label inside the highlighted box. Pass an empty string to drop the label and its icon. */
    fixLabel?: string;
    /** Cards per row on desktop. Defaults to 2. */
    columns?: 2 | 3;
}

const gridSxFor = (columns: number) =>
    ({
        display: 'grid',
        // One card per row on phones, two on tablets, `columns` on desktop. Let CSS flow the
        // cards — never pre-chunk them into rows, or narrower breakpoints orphan the tail.
        gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${columns}, 1fr)`},
        gap: spacing[32]
    } as const);

const cardSx = {
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[16]
} as const;

const cardHeaderSx = {display: 'flex', gap: spacing[16], alignItems: 'flex-start'} as const;

const iconBgSx = {
    bgcolor: colors.background.tag,
    borderRadius: borderRadius.lg,
    width: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
} as const;

const cardTitleWrapSx = {display: 'flex', alignItems: 'center', height: 48} as const;

const cardTitleSx = {fontSize: fontSize[22], color: colors.text.primary} as const;

const challengeLabelSx = {
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    color: colors.neutral.slate400,
    letterSpacing: letterSpacing.half,
    textTransform: 'uppercase',
    mb: spacing[8]
} as const;

const challengeTextSx = {fontSize: fontSize[16]} as const;

const fixBoxSx = {
    bgcolor: colors.background.tag,
    border: `1px solid ${colors.background.tag}`,
    borderLeft: `4px solid ${colors.primary.main}`,
    borderRadius: borderRadius['6'],
    p: spacing[16],
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        borderColor: colors.primary.deep,
        boxShadow: shadows.lg,
        transform: 'scale(1.02)'
    }
} as const;

const fixHeaderSx = {display: 'flex', alignItems: 'center', gap: spacing[8]} as const;

const fixIconWrapSx = {width: 14, height: 14, position: 'relative', flexShrink: 0} as const;

const fixLabelSx = {fontSize: fontSize[14], color: colors.primary.main} as const;

const fixTextSx = {fontSize: fontSize[16]} as const;

const tagWrapSx = {display: 'flex', flexWrap: 'wrap', gap: spacing[8]} as const;

const tagSx = {
    display: 'inline-flex',
    alignItems: 'center',
    px: spacing[12],
    py: spacing[4],
    borderRadius: borderRadius.pill,
    bgcolor: colors.background.tag,
    border: `${borderWidth.thin} solid ${colors.accent.blueBorder}`
} as const;

const tagTextSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.medium,
    color: colors.primary.deep,
    lineHeight: lineHeight[20]
} as const;

const Web3ProblemCards = ({
    heading,
    subheading,
    ourFixIcon,
    cards,
    challengeLabel = 'THE CHALLENGE',
    fixLabel = 'Our Fix',
    columns = 2
}: Web3ProblemCardsProps) => {
    const gridSx = gridSxFor(columns);

    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        {heading}
                    </Typography>
                    <Typography className="font-regular-text">{subheading}</Typography>
                </Box>

                <Box sx={gridSx}>
                    {cards.map((card) => {
                        const iconPx = card.iconSize + 'px';
                        return (
                            <Box key={card.title} sx={cardSx}>
                                <Box sx={cardHeaderSx}>
                                    <Box sx={iconBgSx}>
                                        <Box sx={{width: iconPx, height: iconPx, position: 'relative'}}>
                                            <DisplayImage src={card.icon} alt={card.title} fill />
                                        </Box>
                                    </Box>
                                    <Box sx={cardTitleWrapSx}>
                                        <Typography component="h3" className="font-headers" sx={cardTitleSx}>
                                            {card.title}
                                        </Typography>
                                    </Box>
                                </Box>

                                {!!card.tags?.length && (
                                    <Box sx={tagWrapSx}>
                                        {card.tags.map((tag) => (
                                            <Box key={tag} sx={tagSx}>
                                                <Typography component="span" className="font-headers" sx={tagTextSx}>
                                                    {tag}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                <Box>
                                    {challengeLabel && (
                                        <Typography className="font-headers" sx={challengeLabelSx}>
                                            {challengeLabel}
                                        </Typography>
                                    )}
                                    <Typography className="font-regular-text remove-underline" sx={challengeTextSx}>
                                        {card.challenge}
                                    </Typography>
                                </Box>

                                <Box sx={fixBoxSx}>
                                    {fixLabel && (
                                        <Box sx={fixHeaderSx}>
                                            <Box sx={fixIconWrapSx}>
                                                <DisplayImage src={ourFixIcon} alt="Our fix" fill />
                                            </Box>
                                            <Typography className="font-headers" sx={fixLabelSx}>
                                                {fixLabel}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Typography className="font-regular-text remove-underline" sx={fixTextSx}>
                                        {parse(card.fix)}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(Web3ProblemCards);
